import React, { useState } from 'react';
import {
    ArrowLeft, ChevronDown, ChevronRight, Lock, CheckCircle2, PlayCircle, BookOpen,
    Mic, Brain, FileText, Headphones, Trophy, Gem, Users, Clock, Sparkles,
    Volume2, PenTool, Eye, Ear, Target, Award, MessageCircle, Video
} from 'lucide-react';
import { IELTS_ACADEMIC_COURSE, ACADEMIC_TOTAL_MODULES, ACADEMIC_TOTAL_DAYS, CourseSection, CourseModule } from '../../../data/IELTSCourseData';

interface IELTSAcademicCourseProps {
    onBack: () => void;
    selectedTest: string;
    selectedCountry: { name: string; flag: string };
}

const sectionIcons: Record<number, React.ReactNode> = {
    1: <BookOpen size={20} />,
    2: <Mic size={20} />,
    3: <PenTool size={20} />,
    4: <Eye size={20} />,
    5: <Ear size={20} />,
    6: <Trophy size={20} />,
    7: <Gem size={20} />,
    8: <Users size={20} />,
};

const IELTSAcademicCourse: React.FC<IELTSAcademicCourseProps> = ({ onBack, selectedTest, selectedCountry }) => {
    const [expandedSection, setExpandedSection] = useState<number | null>(1);
    const [activeModule, setActiveModule] = useState<CourseModule | null>(null);
    const [completedModules, setCompletedModules] = useState<Set<number>>(new Set());
    const [activeModuleSection, setActiveModuleSection] = useState<CourseSection | null>(null);

    const toggleSection = (sectionId: number) => {
        setExpandedSection(expandedSection === sectionId ? null : sectionId);
    };

    const openModule = (mod: CourseModule, section: CourseSection) => {
        setActiveModule(mod);
        setActiveModuleSection(section);
    };

    const markComplete = (moduleId: number) => {
        setCompletedModules(prev => {
            const next = new Set(prev);
            next.add(moduleId);
            return next;
        });
        setActiveModule(null);
        setActiveModuleSection(null);
    };

    const progress = Math.round((completedModules.size / ACADEMIC_TOTAL_MODULES) * 100);

    // ── Module Detail View ──
    if (activeModule && activeModuleSection) {
        return (
            <div className="max-w-5xl mx-auto pb-20 animate-fadeIn">
                <button
                    onClick={() => { setActiveModule(null); setActiveModuleSection(null); }}
                    className="flex items-center text-slate-500 hover:text-slate-800 mb-8 transition-all font-bold text-sm bg-white px-5 py-3 rounded-xl shadow-sm border border-slate-100 group w-fit"
                >
                    <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" />
                    Back to Course
                </button>

                <div className="bg-[#011E26] rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-white/5">
                    {/* Module Header */}
                    <div className="relative p-10 md:p-14">
                        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full blur-[100px] -mr-32 -mt-32 pointer-events-none"
                            style={{ background: `${activeModuleSection.color}15` }} />
                        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none opacity-20" />

                        <div className="relative z-10">
                            <div className="flex items-center gap-3 mb-6">
                                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] border border-white/10"
                                    style={{ color: activeModuleSection.color, background: `${activeModuleSection.color}15` }}>
                                    {activeModuleSection.icon} {activeModuleSection.title}
                                </span>
                                {activeModule.day && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] text-white/50 bg-white/5 border border-white/5">
                                        <Clock size={10} /> {activeModule.day}
                                    </span>
                                )}
                            </div>

                            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-3 tracking-tight">
                                Module {activeModule.id}: {activeModule.title}
                            </h2>

                            {/* Feature badges */}
                            <div className="flex flex-wrap gap-2 mt-6">
                                {activeModule.hasAIDrills && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] bg-[#009B8F]/20 text-[#1DE9B6] border border-[#009B8F]/30">
                                        <Brain size={12} /> AI Scoring
                                    </span>
                                )}
                                {activeModule.hasMiniMock && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                        <Target size={12} /> Mini Mock
                                    </span>
                                )}
                                {activeModule.hasAudioPractice && (
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] bg-purple-500/20 text-purple-400 border border-purple-500/30">
                                        <Volume2 size={12} /> Audio Practice
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Module Content */}
                    <div className="bg-white p-10 md:p-14">
                        <h3 className="text-lg font-display font-bold text-[#011E26] mb-8">What You'll Learn</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
                            {activeModule.topics.map((topic, i) => (
                                <div key={i} className="flex items-start gap-4 p-5 rounded-2xl bg-slate-50 border border-slate-100 hover:border-[#009B8F]/30 transition-colors group">
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5"
                                        style={{ background: `${activeModuleSection.color}15` }}>
                                        <span className="text-xs font-bold" style={{ color: activeModuleSection.color }}>{i + 1}</span>
                                    </div>
                                    <div>
                                        <p className="text-sm font-ui font-medium text-[#191c1e] leading-relaxed">{topic}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button className="flex-1 flex items-center justify-center gap-3 bg-gradient-to-r from-[#009B8F] to-[#1DE9B6] text-[#011E26] px-8 py-4 rounded-2xl font-bold text-sm hover:scale-[1.02] transition-all shadow-lg shadow-[#009B8F]/20 active:scale-95">
                                <PlayCircle size={20} />
                                Start Module
                            </button>

                            {activeModule.hasMiniMock && (
                                <button className="flex-1 flex items-center justify-center gap-3 bg-[#011E26] text-white px-8 py-4 rounded-2xl font-bold text-sm hover:bg-[#012E36] transition-all border border-white/10">
                                    <Target size={20} className="text-[#1DE9B6]" />
                                    Take Mini Mock
                                </button>
                            )}

                            <button
                                onClick={() => markComplete(activeModule.id)}
                                className={`flex items-center justify-center gap-3 px-8 py-4 rounded-2xl font-bold text-sm transition-all border ${completedModules.has(activeModule.id)
                                    ? 'bg-[#009B8F]/10 text-[#009B8F] border-[#009B8F]/30'
                                    : 'bg-white text-slate-500 border-slate-200 hover:border-[#009B8F]/40 hover:text-[#009B8F]'
                                    }`}
                            >
                                <CheckCircle2 size={20} />
                                {completedModules.has(activeModule.id) ? 'Completed' : 'Mark Complete'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Course Overview ──
    return (
        <div className="max-w-6xl mx-auto pb-20 animate-fadeIn">
            <button
                onClick={onBack}
                className="flex items-center text-slate-500 hover:text-slate-800 mb-8 transition-all font-bold text-sm bg-white px-5 py-3 rounded-xl shadow-sm border border-slate-100 group w-fit"
            >
                <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" />
                Back
            </button>

            {/* Course Hero */}
            <div className="bg-[#011E26] rounded-[2.5rem] overflow-hidden shadow-2xl ring-1 ring-white/5 mb-10">
                <div className="relative p-10 md:p-16 text-center">
                    {/* Atmospheric effects */}
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#009B8F]/10 rounded-full blur-[120px] -mr-48 -mt-48 pointer-events-none" />
                    <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[#1DE9B6]/5 rounded-full blur-[120px] -ml-48 -mb-48 pointer-events-none" />
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none opacity-20" />

                    <div className="relative z-10">
                        {/* Badges */}
                        <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
                            <span className="inline-flex items-center gap-2 bg-[#009B8F]/20 text-[#1DE9B6] px-4 py-2 rounded-full font-bold text-[10px] tracking-[0.15em] uppercase border border-[#009B8F]/30 backdrop-blur-md">
                                <Sparkles size={12} /> Premium Course
                            </span>
                            <span className="inline-flex items-center gap-2 bg-white/5 text-white/70 px-4 py-2 rounded-full font-bold text-[10px] tracking-[0.15em] uppercase border border-white/10 backdrop-blur-md">
                                <Clock size={12} /> {ACADEMIC_TOTAL_DAYS} Days
                            </span>
                            <span className="inline-flex items-center gap-2 bg-white/5 text-white/70 px-4 py-2 rounded-full font-bold text-[10px] tracking-[0.15em] uppercase border border-white/10 backdrop-blur-md">
                                <BookOpen size={12} /> {ACADEMIC_TOTAL_MODULES} Modules
                            </span>
                        </div>

                        <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4 tracking-tight leading-tight">
                            IELTS Academic <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#009B8F] to-[#1DE9B6]">30-Day Fast-Track</span>
                        </h1>
                        <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed mb-8">
                            A structured, AI-powered preparation course designed to maximise your IELTS Academic score in 30 days.
                        </p>

                        {/* Country & Test badge */}
                        <div className="inline-flex items-center gap-3 bg-white/5 backdrop-blur-md px-5 py-2.5 rounded-full border border-white/10 mb-10">
                            <span className="text-xl">{selectedCountry.flag}</span>
                            <span className="text-white text-xs font-bold uppercase tracking-wider">{selectedCountry.name}</span>
                            <span className="text-white/20">|</span>
                            <span className="text-[#1DE9B6] text-xs font-bold uppercase tracking-wider">{selectedTest}</span>
                        </div>

                        {/* Progress Bar */}
                        <div className="max-w-md mx-auto">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/50">Course Progress</span>
                                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#1DE9B6]">{progress}%</span>
                            </div>
                            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                <div
                                    className="h-full rounded-full transition-all duration-700 ease-out"
                                    style={{
                                        width: `${progress}%`,
                                        background: 'linear-gradient(90deg, #009B8F, #1DE9B6)',
                                    }}
                                />
                            </div>
                            <p className="text-white/30 text-xs mt-2">{completedModules.size} of {ACADEMIC_TOTAL_MODULES} modules completed</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section List */}
            <div className="space-y-4">
                {IELTS_ACADEMIC_COURSE.map((section) => {
                    const isExpanded = expandedSection === section.id;
                    const sectionCompleted = section.modules.every(m => completedModules.has(m.id));
                    const sectionProgress = section.modules.filter(m => completedModules.has(m.id)).length;

                    return (
                        <div key={section.id} className="rounded-2xl overflow-hidden transition-all duration-300"
                            style={{
                                background: '#ffffff',
                                boxShadow: isExpanded
                                    ? '0 4px 24px rgba(0,52,52,0.08), 0 8px 32px rgba(0,52,52,0.05)'
                                    : '0 1px 3px rgba(0,52,52,0.04), 0 4px 16px rgba(0,52,52,0.03)',
                            }}>
                            {/* Section Header */}
                            <button
                                onClick={() => toggleSection(section.id)}
                                className="w-full flex items-center gap-5 p-6 text-left group transition-colors hover:bg-slate-50/50"
                            >
                                {/* Section Icon */}
                                <div className="flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110"
                                    style={{ background: `${section.color}15`, color: section.color }}>
                                    {sectionIcons[section.id]}
                                </div>

                                {/* Section Info */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 mb-1">
                                        <h3 className="text-base font-display font-bold text-[#191c1e] truncate">
                                            Section {section.id}: {section.title}
                                        </h3>
                                        {sectionCompleted && (
                                            <CheckCircle2 size={16} className="text-[#009B8F] flex-shrink-0" />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="text-xs font-ui text-slate-400">{section.dayRange}</span>
                                        <span className="text-xs font-ui text-slate-400">{section.modules.length} module{section.modules.length !== 1 ? 's' : ''}</span>
                                        <span className="text-xs font-ui font-bold" style={{ color: section.color }}>
                                            {sectionProgress}/{section.modules.length} done
                                        </span>
                                    </div>
                                </div>

                                {/* Progress mini-bar */}
                                <div className="hidden sm:block w-24">
                                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                        <div className="h-full rounded-full transition-all duration-500"
                                            style={{
                                                width: `${(sectionProgress / section.modules.length) * 100}%`,
                                                background: section.color,
                                            }} />
                                    </div>
                                </div>

                                {/* Chevron */}
                                <ChevronDown
                                    size={20}
                                    className={`text-slate-300 transition-transform duration-300 flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                                />
                            </button>

                            {/* Module Cards */}
                            {isExpanded && (
                                <div className="px-6 pb-6 space-y-3 animate-fadeIn">
                                    <div className="border-t border-slate-100 pt-4" />
                                    {section.modules.map((mod) => {
                                        const isCompleted = completedModules.has(mod.id);
                                        return (
                                            <div
                                                key={mod.id}
                                                onClick={() => openModule(mod, section)}
                                                className={`relative flex items-center gap-5 p-5 rounded-xl cursor-pointer transition-all duration-200 group border ${isCompleted
                                                    ? 'bg-[#009B8F]/5 border-[#009B8F]/20'
                                                    : 'bg-slate-50 border-slate-100 hover:border-[#009B8F]/30 hover:bg-[#009B8F]/5'
                                                    }`}
                                            >
                                                {/* Module Number */}
                                                <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold transition-colors ${isCompleted
                                                    ? 'bg-[#009B8F] text-white'
                                                    : 'bg-white text-[#191c1e] border border-slate-200 group-hover:border-[#009B8F]/40 group-hover:text-[#009B8F]'
                                                    }`}>
                                                    {isCompleted ? <CheckCircle2 size={18} /> : mod.id}
                                                </div>

                                                {/* Module Info */}
                                                <div className="flex-1 min-w-0">
                                                    <h4 className={`text-sm font-display font-bold mb-1 truncate ${isCompleted ? 'text-[#009B8F]' : 'text-[#191c1e]'}`}>
                                                        Module {mod.id}: {mod.title}
                                                    </h4>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        {mod.day && (
                                                            <span className="text-[10px] font-ui text-slate-400 uppercase tracking-wider">{mod.day}</span>
                                                        )}
                                                        {mod.topics.length > 0 && (
                                                            <span className="text-[10px] font-ui text-slate-300">•</span>
                                                        )}
                                                        <span className="text-[10px] font-ui text-slate-400">{mod.topics.length} topics</span>

                                                        {/* Feature tags */}
                                                        {mod.hasAIDrills && (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider bg-[#009B8F]/10 text-[#009B8F]">
                                                                <Brain size={8} /> AI
                                                            </span>
                                                        )}
                                                        {mod.hasMiniMock && (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider bg-amber-500/10 text-amber-600">
                                                                <Target size={8} /> Mock
                                                            </span>
                                                        )}
                                                        {mod.hasAudioPractice && (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider bg-purple-500/10 text-purple-600">
                                                                <Volume2 size={8} /> Audio
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Arrow */}
                                                <ChevronRight size={18} className="text-slate-300 group-hover:text-[#009B8F] group-hover:translate-x-1 transition-all flex-shrink-0" />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Course Footer */}
            <div className="mt-12 bg-[#011E26] rounded-2xl p-8 md:p-10 text-center shadow-lg ring-1 ring-white/5">
                <div className="flex flex-wrap items-center justify-center gap-6 mb-4">
                    <div className="flex items-center gap-2">
                        <Award size={16} className="text-[#1DE9B6]" />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-white/60">Nex Overseas Certified</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Brain size={16} className="text-[#1DE9B6]" />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-white/60">AI-Powered Scoring</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Target size={16} className="text-[#1DE9B6]" />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-white/60">Band 7+ Focused</span>
                    </div>
                </div>
                <p className="text-slate-500 text-xs tracking-wider">
                    Nex Overseas | info@nexoverseas.com | &copy; 2026 Nex Overseas
                </p>
            </div>
        </div>
    );
};

export default IELTSAcademicCourse;
